/**
 * CR204: Results Block
 * CR228: Removed hardcoded DealTypeFilter - now a separate block.
 *        Removed hardcoded empty state - now a separate block.
 * CR320: Wired to layout_json for:
 *        - card_visible search scope (only searches title/condition/reward/merchant)
 *        - layout-driven sorting via context.selectedSort
 *        - pageSize from resultsConfig
 * CR500: Removed hardcoded "X Deals Available" header - now ResultsMetaBlock.
 *        This block is now purely the deal grid renderer.
 * 
 * Renders the deal results grid/list.
 * This is the main content block - always enabled in most layouts.
 * 
 * Includes:
 * - Results grid/list with DealCardUX settings applied
 * - Minimal inline message when filtered results are empty (not global empty state)
 */

import { useMemo } from "react";
import { Search } from "lucide-react";
import type { BlockProps } from "./types";
import type { ResultsBlockConfig, SearchFilterConfig } from "@/lib/layout-studio/types";
import { DEFAULT_SEARCH_FILTER_CONFIG } from "@/lib/layout-studio/types";
import { DealGridWithDetails } from "@/components/deals/DealGridWithDetails";
import { DealCardUXProvider } from "@/contexts/DealCardUXContext";
import { inferDealCategory } from "@/lib/types/deals";
import { getMerchant, getAllMerchants } from "@/lib/merchantIndex";
import { filterDealsBySearch } from "@/lib/deals/cardVisibleSearch";
import { sortDealsByOption } from "@/lib/deals/sortDeals";

/**
 * Normalize a merchant display name to a key format for lookup
 */
function normalizeToMerchantKey(merchantName: string): string {
  return merchantName
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/**
 * Get the promo merchant for a deal (for filtering/grouping).
 */
function getPromoMerchant(deal: { promo_merchant_name?: string; merchant: string }): string {
  return deal.promo_merchant_name || deal.merchant;
}

/**
 * Get category for a deal using multiple fallback strategies
 */
function getDealCategory(deal: { promo_merchant_name?: string; merchant: string }): string | null {
  const merchantName = getPromoMerchant(deal);
  if (!merchantName) return null;
  
  const normalizedKey = normalizeToMerchantKey(merchantName);
  
  // Strategy 1: Direct lookup by normalized key
  const merchant = getMerchant(normalizedKey);
  if (merchant?.category) {
    return merchant.category;
  }
  
  // Strategy 2: Try exact match on display_name
  const allMerchants = getAllMerchants();
  for (const m of allMerchants) {
    if (m.display_name.toLowerCase() === merchantName.toLowerCase()) {
      if (m.category) return m.category;
    }
  }
  
  // Strategy 3: Try partial match
  for (const m of allMerchants) {
    const displayLower = m.display_name.toLowerCase();
    const nameLower = merchantName.toLowerCase();
    if (displayLower.includes(nameLower) || nameLower.includes(displayLower)) {
      if (m.category) return m.category;
    }
  }
  
  return null;
}

export function ResultsBlock({ config, context }: BlockProps<ResultsBlockConfig>) {
  const { dealCardUX, deals, searchQuery, selectedCategory, selectedDealTypes, selectedSort, isAdmin } = context;
  
  // CR320: Get search config from layout for card-visible search
  const searchFilter = context.layout?.filters?.find((f) => f.id === "search");
  const searchConfig = (searchFilter?.config as SearchFilterConfig) || DEFAULT_SEARCH_FILTER_CONFIG;
  
  // CR230: Server visibility is source of truth - if server returned it, display it.
  const confirmedDeals = useMemo(() => {
    return deals;
  }, [deals]);
  
  // Apply all filters and sorting
  const filteredDeals = useMemo(() => {
    let result = [...confirmedDeals];

    // Filter by merchant category if not "All"
    if (selectedCategory !== "All") {
      result = result.filter((deal) => {
        const dealCat = getDealCategory(deal);
        if (selectedCategory === "Unclassified") {
          return dealCat === null;
        }
        return dealCat === selectedCategory;
      });
    }

    // Filter by deal type if not "All"
    if (selectedDealTypes !== "All") {
      result = result.filter((deal) => {
        const dealCategory = inferDealCategory(deal);
        return selectedDealTypes.includes(dealCategory);
      });
    }

    // CR320: Filter by search query using card-visible scope
    if (searchQuery.trim().length >= (searchConfig.minChars || 1)) {
      result = filterDealsBySearch(result, searchQuery, searchConfig);
    }

    // CR320: Apply layout-driven sorting
    result = sortDealsByOption(result, selectedSort);

    return result;
  }, [confirmedDeals, searchQuery, selectedCategory, selectedDealTypes, selectedSort, searchConfig]);

  const isSearching = searchQuery.trim().length >= 1;
  const isCategoryFiltered = selectedCategory !== "All" && searchQuery.trim() === "";
  const isDealTypeFiltered = selectedDealTypes !== "All";
  const isFiltered = isSearching || isCategoryFiltered || isDealTypeFiltered;

  // CR228: If no confirmed deals at all, don't render anything.
  // The EmptyStateBlock handles the global "no deals" state.
  if (confirmedDeals.length === 0) {
    return null;
  }

  // CR228: If filters produced zero results, show a minimal inline message
  if (filteredDeals.length === 0 && isFiltered) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-3">
            <Search className="w-6 h-6 text-muted-foreground" />
          </div>
          <p className="text-base text-foreground">
            {isSearching 
              ? "No deals match your search."
              : isDealTypeFiltered
                ? "No deals found for the selected type(s)."
                : "No deals found in this category."
            }
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Try adjusting your filters.
          </p>
        </div>
      </div>
    );
  }

  // CR500: Removed hardcoded "X Deals Available" header
  // This is now handled by ResultsMetaBlock
  return (
    <div className="space-y-6">
      {/* Deal Cards Grid */}
      <DealCardUXProvider 
        cardSize={dealCardUX.cardSize} 
        density={dealCardUX.density}
        detailsMode={dealCardUX.detailsMode}
        expandSingleOpen={dealCardUX.expandSingleOpen}
        detailsSections={dealCardUX.detailsSections}
        customHeightsEnabled={dealCardUX.customHeightsEnabled}
        customHeightsPx={dealCardUX.customHeightsPx}
        flyerInteraction={dealCardUX.flyerInteraction}
        feedbackWidget={dealCardUX.feedbackWidget}
      >
        <DealGridWithDetails
          deals={filteredDeals}
          isAdmin={isAdmin}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        />
      </DealCardUXProvider>
    </div>
  );
}
